/**
 * Set-Based Degree Requirement Optimization
 * 
 * This module implements a set-based approach to find the minimum number of courses
 * needed to satisfy degree requirements by analyzing unions and intersections of
 * requirement sets.
 */

export interface RequirementSet {
  id: string;
  title: string;
  coursePool: Set<string>; // Expanded set of all courses that can satisfy this requirement (normalized codes)
  coursesNeeded?: number;
  creditsNeeded?: number;
  logic: 'and' | 'or';
  children?: RequirementSet[];
  parentId?: string;
  isOrGroupChild?: boolean; // True if this is a child of an OR group
  orGroupId?: string; // ID of the parent OR group, if applicable
}

export interface CourseCoverage {
  course: string; // Original course code (with colons)
  normalizedCode: string; // Normalized for comparison
  satisfiesRequirements: Set<string>; // Requirement IDs this course satisfies
  credits: number;
  coverageScore: number; // Number of requirements satisfied per credit (efficiency metric)
}

export interface OptimizedCourseSelection {
  courses: string[]; // Course codes in original format
  totalCredits: number;
  requirementSatisfaction: Map<string, {
    satisfied: boolean;
    coursesUsed: string[];
    creditsUsed: number;
    needed: number; // courses_needed or credits_needed
  }>;
  optimizationScore: number; // Courses saved vs naive sequential approach
  coverageDetails: Map<string, CourseCoverage>; // Course -> coverage info
}

interface CourseInfo {
  credits: number;
  name?: string;
}

/**
 * Normalizes a course code for comparison (strips non-numeric characters)
 * This matches the normalization used throughout the codebase
 */
function normalizeCourseCode(code: string): string {
  return String(code).replace(/[^0-9]/g, '');
}

/**
 * Checks if a string looks like a course code (e.g., "01:198:111" or "01198111")
 */
function isLikelyCourseCode(entry: string): boolean {
  const normalized = normalizeCourseCode(entry);
  return normalized.length >= 6 && /^\d+$/.test(normalized);
}

/**
 * Builds requirement sets from the degree requirement tree structure
 * Fixed: Properly handles OR groups and tracks parent relationships
 */
export function buildRequirementSets(
  requirements: any[],
  expandedPools: Map<string, string[]>,
  normalize: (code: string) => string = normalizeCourseCode,
  parentId?: string,
  parentLogic?: 'and' | 'or',
  orGroupId?: string
): RequirementSet[] {
  const sets: RequirementSet[] = [];

  for (const req of requirements) {
    const setId = req.id || `${parentId || 'root'}_${req.title?.replace(/\s+/g, '_') || 'req'}`;
    const currentLogic = req.logic?.toLowerCase() || 'and';
    const currentOrGroupId = currentLogic === 'or' ? setId : orGroupId;

    // If this requirement has nested requirements, process them recursively
    if (req.requirements && Array.isArray(req.requirements) && req.requirements.length > 0) {
      const childSets = buildRequirementSets(
        req.requirements,
        expandedPools,
        normalize,
        setId,
        currentLogic,
        currentOrGroupId
      );

      if (currentLogic === 'and') {
        // AND groups: add all child sets
        sets.push(...childSets);
      } else {
        // OR group: mark children as OR group children and track the OR group
        const markedChildren = childSets.map(child => ({
          ...child,
          isOrGroupChild: true,
          orGroupId: setId
        }));

        sets.push(...markedChildren);

        // Also create a virtual requirement set to track the OR group itself
        // This will be used to check if the OR group is satisfied
        const orGroupPool = new Set<string>();
        markedChildren.forEach(child => {
          child.coursePool.forEach(code => orGroupPool.add(code));
        });

        sets.push({
          id: setId,
          title: req.title || 'OR Group',
          coursePool: orGroupPool,
          logic: 'or',
          children: markedChildren,
          parentId,
          coursesNeeded: req.courses_needed,
          creditsNeeded: req.credits_needed
        });
      }
    }

    // If this requirement has a course pool, create a requirement set
    if (req.course_pool && Array.isArray(req.course_pool) && req.course_pool.length > 0) {
      const coursePool = new Set<string>();

      // Expand the course pool
      for (const entry of req.course_pool) {
        if (!expandedPools.has(entry) && isLikelyCourseCode(entry)) {
          coursePool.add(normalize(entry));
        } else {
          // It's a course list reference - expand it
          const expanded = expandedPools.get(entry);
          if (expanded && Array.isArray(expanded)) {
            expanded.forEach(code => {
              if (isLikelyCourseCode(code)) {
                coursePool.add(normalize(code));
              }
            });
          }
        }
      }

      if (coursePool.size > 0) {
        sets.push({
          id: setId,
          title: req.title || 'Requirement',
          coursePool,
          coursesNeeded: req.courses_needed,
          creditsNeeded: req.credits_needed,
          logic: currentLogic,
          parentId,
          isOrGroupChild: currentLogic === 'or' || !!orGroupId,
          orGroupId: currentOrGroupId
        });
      }
    }
  }

  return sets;
}

/**
 * Analyzes which requirements each course satisfies (intersection analysis)
 * Fixed: Preserves original course code format while using normalized codes for comparison
 */
export function analyzeCourseCoverage(
  requirementSets: RequirementSet[],
  courseUniverse: Map<string, CourseInfo>,
  normalize: (code: string) => string = normalizeCourseCode
): Map<string, CourseCoverage> {
  const coverage = new Map<string, CourseCoverage>();
  const normalizedToOriginal = new Map<string, string>(); // Map normalized -> original format

  // First pass: build mapping of normalized codes to original codes
  for (const [courseCode, courseInfo] of courseUniverse) {
    const normalizedCode = normalize(courseCode);
    // Store the first (or best) original format for each normalized code
    if (!normalizedToOriginal.has(normalizedCode) || courseCode.includes(':')) {
      normalizedToOriginal.set(normalizedCode, courseCode);
    }
  }

  // Second pass: analyze coverage
  for (const [courseCode, courseInfo] of courseUniverse) {
    const normalizedCode = normalize(courseCode);
    const satisfiesRequirements = new Set<string>();

    // Check which requirement sets include this course
    for (const reqSet of requirementSets) {
      // Skip OR group parent nodes - only check actual requirement sets
      if (reqSet.logic === 'or' && reqSet.children && reqSet.coursePool.size === 0) {
        continue;
      }

      if (reqSet.coursePool.has(normalizedCode)) {
        satisfiesRequirements.add(reqSet.id);
      }
    }

    if (satisfiesRequirements.size > 0) {
      // Calculate coverage score: requirements satisfied per credit
      const coverageScore = satisfiesRequirements.size / courseInfo.credits;

      // Use the original course code format
      const originalCode = normalizedToOriginal.get(normalizedCode) || courseCode;

      coverage.set(originalCode, {
        course: originalCode,
        normalizedCode,
        satisfiesRequirements,
        credits: courseInfo.credits,
        coverageScore
      });
    }
  }

  return coverage;
}

/**
 * Greedy set cover algorithm to find minimum course selection
 * Fixed: Properly handles OR groups, partial satisfaction, and early termination
 */
export function greedySetCover(
  requirementSets: RequirementSet[],
  courseCoverage: Map<string, CourseCoverage>,
  completedCourses: Set<string>,
  normalize: (code: string) => string = normalizeCourseCode
): OptimizedCourseSelection {
  const selectedCourses: string[] = [];
  const requirementSatisfaction = new Map<string, {
    satisfied: boolean;
    coursesUsed: string[];
    creditsUsed: number;
    needed: number;
  }>();

  // Track OR groups separately
  const orGroups = new Map<string, {
    id: string;
    children: RequirementSet[];
    satisfied: boolean;
  }>();

  // Initialize satisfaction tracking
  for (const reqSet of requirementSets) {
    // Skip OR group parent nodes that are just for tracking
    if (reqSet.logic === 'or' && reqSet.children && reqSet.coursePool.size === 0) {
      // This is an OR group parent - track it separately
      orGroups.set(reqSet.id, {
        id: reqSet.id,
        children: reqSet.children || [],
        satisfied: false
      });
      continue;
    }

    requirementSatisfaction.set(reqSet.id, {
      satisfied: false,
      coursesUsed: [],
      creditsUsed: 0,
      needed: reqSet.coursesNeeded || reqSet.creditsNeeded || 0
    });
  }

  // Account for already completed courses
  const normalizedCompleted = new Set(Array.from(completedCourses).map(normalize));

  for (const [courseCode, coverage] of courseCoverage) {
    const normalizedCode = normalize(courseCode);

    if (normalizedCompleted.has(normalizedCode)) {
      // This course is already completed - count it toward requirements
      for (const reqId of coverage.satisfiesRequirements) {
        const satisfaction = requirementSatisfaction.get(reqId);
        if (!satisfaction) continue;

        const reqSet = requirementSets.find(r => r.id === reqId);
        if (!reqSet) continue;

        if (reqSet.coursesNeeded) {
          satisfaction.coursesUsed.push(courseCode);
          if (satisfaction.coursesUsed.length >= satisfaction.needed) {
            satisfaction.satisfied = true;
          }
        } else if (reqSet.creditsNeeded) {
          satisfaction.creditsUsed += coverage.credits;
          if (satisfaction.creditsUsed >= satisfaction.needed) {
            satisfaction.satisfied = true;
          }
        }
      }
    }
  }

  // Second pass: credit completed courses directly against requirement pools
  // This catches courses not in the current catalog (e.g., taken in a prior semester)
  for (const completedCode of normalizedCompleted) {
    for (const reqSet of requirementSets) {
      // Skip OR group parents
      if (reqSet.logic === 'or' && reqSet.children && reqSet.coursePool.size === 0) continue;

      const satisfaction = requirementSatisfaction.get(reqSet.id);
      if (!satisfaction || satisfaction.satisfied) continue;

      // Check if this completed course is in the requirement's pool
      if (reqSet.coursePool.has(completedCode)) {
        // Avoid double-counting — only add if not already counted from the catalog pass
        const alreadyCounted = satisfaction.coursesUsed.some(
          used => normalize(used) === completedCode
        );
        if (alreadyCounted) continue;



        if (reqSet.coursesNeeded) {
          satisfaction.coursesUsed.push(completedCode);
          if (satisfaction.coursesUsed.length >= satisfaction.needed) {
            satisfaction.satisfied = true;
          }
        } else if (reqSet.creditsNeeded) {
          satisfaction.creditsUsed += 3;
          if (satisfaction.creditsUsed >= satisfaction.needed) {
            satisfaction.satisfied = true;
          }
        } else {
          satisfaction.satisfied = true;
          satisfaction.coursesUsed.push(completedCode);
        }
      }
    }
  }



  // Update OR group satisfaction based on completed courses
  for (const [orGroupId, orGroup] of orGroups) {
    const anyChildSatisfied = orGroup.children.some(child => {
      const satisfaction = requirementSatisfaction.get(child.id);
      return satisfaction?.satisfied || false;
    });
    orGroup.satisfied = anyChildSatisfied;
  }

  // Build per-pool selection caps to prevent over-selection from shared pools.
  // When multiple requirement sets share the same course pool (e.g., "Total Honors Courses" 
  // needing 4 and "Interdisciplinary Honors Seminar" needing 1 from the same 6 courses),
  // cap selections from that pool to the minimum courses_needed across all sharing sets.
  const poolCapMap = new Map<string, { cap: number; reqIds: Set<string> }>();
  const reqIdToPoolKey = new Map<string, string>();
  for (const reqSet of requirementSets) {
    if (reqSet.logic === 'or' && reqSet.children && reqSet.coursePool.size === 0) continue;
    if (reqSet.coursePool.size === 0) continue;
    const poolKey = Array.from(reqSet.coursePool).sort().join(',');
    reqIdToPoolKey.set(reqSet.id, poolKey);
    const existing = poolCapMap.get(poolKey);
    const needed = reqSet.coursesNeeded || 1;
    if (existing) {
      existing.cap = Math.min(existing.cap, needed);
      existing.reqIds.add(reqSet.id);
    } else {
      poolCapMap.set(poolKey, { cap: needed, reqIds: new Set([reqSet.id]) });
    }
  }
  const poolSelections = new Map<string, number>(); // poolKey -> count selected so far

  // Sort courses by coverage score (descending) - prioritize courses that satisfy many requirements
  // This ensures courses that overlap across multiple requirements are selected first
  const sortedCourses = Array.from(courseCoverage.values())
    .filter(c => !normalizedCompleted.has(normalize(c.course))) // Exclude completed
    .sort((a, b) => {
      // Primary sort: number of requirements satisfied (prioritize overlap)
      const aReqCount = a.satisfiesRequirements.size;
      const bReqCount = b.satisfiesRequirements.size;
      if (aReqCount !== bReqCount) {
        return bReqCount - aReqCount; // More requirements = higher priority
      }
      // Secondary sort: coverage score (requirements per credit)
      if (Math.abs(a.coverageScore - b.coverageScore) > 0.01) {
        return b.coverageScore - a.coverageScore;
      }
      // Tertiary sort: lower credits (more efficient)
      return a.credits - b.credits;
    });

  // Optimization: Map requirementSets by ID for O(1) lookup in the loop
  const requirementSetsById = new Map<string, RequirementSet>(
    requirementSets.map(r => [r.id, r])
  );

  // Greedy selection
  for (const courseCover of sortedCourses) {
    const courseId = courseCover.course;
    const normalizedCode = normalize(courseId);

    // Check if selecting this course would help satisfy remaining requirements
    let wouldHelp = false;
    const requirementsThisHelps: string[] = [];

    for (const reqId of courseCover.satisfiesRequirements) {
      const satisfaction = requirementSatisfaction.get(reqId);
      if (!satisfaction || satisfaction.satisfied) continue;

      const reqSet = requirementSetsById.get(reqId);
      if (!reqSet) continue;

      // Skip OR group parent nodes
      if (reqSet.logic === 'or' && reqSet.children && reqSet.coursePool.size === 0) {
        continue;
      }

      // Skip OR group children whose parent OR group is already satisfied
      if (reqSet.isOrGroupChild && reqSet.orGroupId) {
        const parentOrGroup = orGroups.get(reqSet.orGroupId);
        if (parentOrGroup?.satisfied) {
          continue;
        }
      }

      // Pool-cap check: if this requirement's pool has already reached its min cap,
      // don't select more courses from it. This prevents over-selection when multiple
      // requirements share the same course pool with different courses_needed values.
      const poolKey = reqIdToPoolKey.get(reqId);
      if (poolKey) {
        const poolInfo = poolCapMap.get(poolKey);
        const currentSelections = poolSelections.get(poolKey) || 0;
        if (poolInfo && currentSelections >= poolInfo.cap) {
          continue; // This pool has reached its selection cap
        }
      }

      if (reqSet.coursesNeeded) {
        if (satisfaction.coursesUsed.length < satisfaction.needed) {
          wouldHelp = true;
          requirementsThisHelps.push(reqId);
        }
      } else if (reqSet.creditsNeeded) {
        if (satisfaction.creditsUsed < satisfaction.needed) {
          wouldHelp = true;
          requirementsThisHelps.push(reqId);
        }
      } else {
        // No explicit count needed - just needs at least one course
        wouldHelp = true;
        requirementsThisHelps.push(reqId);
      }
    }

    if (wouldHelp) {
      selectedCourses.push(courseId);

      // Update pool selection counts for the cap mechanism
      const poolsUpdated = new Set<string>();
      for (const reqId of requirementsThisHelps) {
        const poolKey = reqIdToPoolKey.get(reqId);
        if (poolKey && !poolsUpdated.has(poolKey)) {
          poolsUpdated.add(poolKey);
          poolSelections.set(poolKey, (poolSelections.get(poolKey) || 0) + 1);
        }
      }

      // Update satisfaction counts
      for (const reqId of requirementsThisHelps) {
        const satisfaction = requirementSatisfaction.get(reqId);
        if (!satisfaction) continue;

        const reqSet = requirementSets.find(r => r.id === reqId);
        if (!reqSet) continue;

        if (reqSet.coursesNeeded) {
          satisfaction.coursesUsed.push(courseId);
          if (satisfaction.coursesUsed.length >= satisfaction.needed) {
            satisfaction.satisfied = true;
          }
        } else if (reqSet.creditsNeeded) {
          satisfaction.creditsUsed += courseCover.credits;
          if (satisfaction.creditsUsed >= satisfaction.needed) {
            satisfaction.satisfied = true;
          }
        } else {
          // No explicit count - mark as satisfied
          satisfaction.satisfied = true;
          satisfaction.coursesUsed.push(courseId);
        }
      }

      // Update OR group satisfaction after adding a course
      for (const [orGroupId, orGroup] of orGroups) {
        if (orGroup.satisfied) continue; // Already satisfied

        const anyChildSatisfied = orGroup.children.some(child => {
          const satisfaction = requirementSatisfaction.get(child.id);
          return satisfaction?.satisfied || false;
        });
        orGroup.satisfied = anyChildSatisfied;
      }
    }

    // Early termination: check if all requirements are satisfied
    // Fixed: Exclude OR group children from the regular check — their satisfaction
    // is tracked through the parent OR group instead
    const allRegularRequirementsSatisfied = Array.from(requirementSatisfaction.entries())
      .every(([id, s]) => {
        const reqSet = requirementSets.find(r => r.id === id);
        // OR group children are checked via their parent OR group, not individually
        if (reqSet?.isOrGroupChild && reqSet.orGroupId) return true;
        return s.satisfied;
      });

    const allOrGroupsSatisfied = Array.from(orGroups.values())
      .every(og => og.satisfied);

    if (allRegularRequirementsSatisfied && allOrGroupsSatisfied) {
      break;
    }
  }

  // Final pass: ensure OR groups are properly marked
  for (const [orGroupId, orGroup] of orGroups) {
    const anyChildSatisfied = orGroup.children.some(child => {
      const satisfaction = requirementSatisfaction.get(child.id);
      return satisfaction?.satisfied || false;
    });
    orGroup.satisfied = anyChildSatisfied;
  }

  // Calculate total credits
  const totalCredits = selectedCourses.reduce((sum, code) => {
    const coverage = courseCoverage.get(code);
    return sum + (coverage?.credits || 0);
  }, 0);

  // Calculate optimization score (naive approach would select all requirements sequentially)
  const naiveCourseCount = requirementSets
    .filter(r => {
      // Exclude OR group parent nodes and OR group children (count OR groups as 1)
      if (r.logic === 'or' && r.children && r.coursePool.size === 0) return false;
      if (r.isOrGroupChild) return false; // Don't double-count OR children
      return true;
    })
    .reduce((sum, req) => {
      if (req.coursesNeeded) return sum + req.coursesNeeded;
      if (req.creditsNeeded) return sum + Math.ceil(req.creditsNeeded / 3); // Assume 3 credits per course
      return sum + 1;
    }, 0);

  const optimizationScore = naiveCourseCount - selectedCourses.length;

  return {
    courses: selectedCourses,
    totalCredits,
    requirementSatisfaction,
    optimizationScore,
    coverageDetails: courseCoverage
  };
}

/**
 * Main function: Calculate optimal course set for degree requirements
 */
export function calculateOptimalCourseSet(
  requirements: any[],
  completedCourses: string[],
  expandedPools: Map<string, string[]>,
  courseUniverse: Map<string, CourseInfo>,
  normalize: (code: string) => string = normalizeCourseCode
): OptimizedCourseSelection {
  // Step 1: Build requirement sets
  const requirementSets = buildRequirementSets(requirements, expandedPools, normalize);

  // Step 2: Analyze course coverage (intersections)
  const courseCoverage = analyzeCourseCoverage(requirementSets, courseUniverse, normalize);

  // Step 3: Run greedy set cover
  const completedSet = new Set(completedCourses.map(normalize));
  const result = greedySetCover(requirementSets, courseCoverage, completedSet, normalize);

  return result;
}

